import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import moment from 'moment';
import SettingsIcon from '@mui/icons-material/Settings';
import ComputerIcon from '@mui/icons-material/Computer';
import CodeIcon from '@mui/icons-material/Code';

import EditIcon from '../../../GlobalComponents/IconComponents/EditIcon';
import ExecuteIcon from '../../../GlobalComponents/IconComponents/ExcecuteIcon';
import ViewIcon from '../../../GlobalComponents/IconComponents/ViewIcon';
import { Colors } from '../../../GlobalComponents/Utility/Colors';
import { array_status } from '../../../ReduxTLK/features/SAV/ticketList';
import {
  translateCanalIndexLabel,
  translateDelaiIndexLabel,
} from '../../../utils/labelTranslator';
import StatusTicket from './StatusTicket';

import styles from './ticketListColumns.module.css';

/* ---------- Helpers ---------- */

const capitalize = (text) =>
  typeof text === 'string'
    ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
    : text;

const renderEmpty = () => <span className={styles.cellEmpty}>—</span>;

const getNoteClass = (rate) => {
  const n = Number(rate);
  if (n >= 4) return styles.noteHigh;
  if (n >= 2.5) return styles.noteMid;
  return styles.noteLow;
};

/* ---------- Sub-components ---------- */

const InternalStatusCell = ({ statutIntern }) => {
  const config = {
    0: {
      label: 'Paramétrage',
      cls: styles.internStatusParam,
      icon: <SettingsIcon sx={{ fontSize: 13 }} />,
    },
    1: {
      label: 'IT',
      cls: styles.internStatusIT,
      icon: <ComputerIcon sx={{ fontSize: 13 }} />,
    },
    2: {
      label: 'Dev',
      cls: styles.internStatusDev,
      icon: <CodeIcon sx={{ fontSize: 13 }} />,
    },
  };

  const item = config[statutIntern];
  if (!item) return <span className={styles.cellEmpty}>—</span>;

  return (
    <Tooltip title={item.label} arrow placement="top">
      <span className={`${styles.internStatus} ${item.cls}`}>
        {item.icon}
        <span className={styles.internStatusLabel}>{item.label}</span>
      </span>
    </Tooltip>
  );
};

const ActionButton = ({ tooltip, onClick, children, variant }) => (
  <Tooltip title={tooltip} arrow placement="top">
    <IconButton
      size="small"
      className={`${styles.actionBtn} ${variant === 'execute' ? styles.actionBtnExecute : ''
        }`}
      onClick={onClick}
    >
      {children}
    </IconButton>
  </Tooltip>
);

/* ---------- Column definitions ---------- */

export const createColumns = (handleView, handleEdit, handleExecute, isSmallScreen) => {
  /* Responsive width weights — on small screens we give more flex to essential columns */
  const w = isSmallScreen
    ? {
      id: 0.08,
      pdv: 0.22,
      sujet: 0.22,
      date: 0.13,
      assigne: 0.12,
      rate: 0.08,
      auteur: 0.12,
      canal: 0.1,
      delai: 0.12,
      statut: 0.1,
      statutInt: 0.1,
      action: 0.12,
    }
    : {
      id: 0.06,
      pdv: 0.18,
      sujet: 0.25,
      date: 0.1,
      assigne: 0.1,
      rate: 0.1,
      auteur: 0.1,
      canal: 0.08,
      delai: 0.1,
      statut: 0.1,
      statutInt: 0.09,
      action: 0.1,
    };

  return [
    {
      field: 'id',
      headerName: 'Référence',
      flex: w.id,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params?.value ? (
          <span className={styles.refCell}>#{params.value}</span>
        ) : (
          renderEmpty()
        ),
    },

    {
      field: 'pointDeVente',
      headerName: 'Établissement',
      flex: w.pdv,
      minWidth: 140,
      renderCell: (params) => {
        const chain = capitalize(params.row.chaine);
        const pv = capitalize(params.row.pointDeVente);
        const initial = params.row?.chaine?.slice(0, 2)?.toUpperCase() || '?';

        return (
          <div className={styles.establishmentCell}>
            <div className={styles.avatar}>{initial}</div>
            <div className={styles.establishmentText}>
              <span className={styles.chainName} title={chain}>
                {chain}
              </span>
              <span className={styles.pvName} title={params.row.pointDeVente}>
                {pv}
              </span>
            </div>
          </div>
        );
      },
    },

    {
      field: 'sujet',
      headerName: 'Sujet',
      flex: w.sujet,
      minWidth: 120,
      renderCell: (params) => {
        const subject = capitalize(params?.value);
        if (!subject) return renderEmpty();

        return (
          <Tooltip title={subject} placement="top" arrow>
            <span className={styles.subjectCell}>{subject}</span>
          </Tooltip>
        );
      },
    },

    {
      field: 'date',
      headerName: 'Date',
      flex: w.date,
      minWidth: 100,
      renderCell: (params) => {
        if (!params?.value) return renderEmpty();
        const m = moment(params.value);
        return (
          <div className={styles.dateCell}>
            <span className={styles.dateDay}>{m.format('DD MMM YYYY')}</span>
            <span className={styles.dateTime}>{m.format('HH:mm')}</span>
          </div>
        );
      },
    },
    // first agent Assigne
    {
      field: 'assigne',
      headerName: 'Assigné à',
      flex: w.assigne,
      minWidth: 90,
      renderCell: (params) =>
        params.row.assigne ? (
          <Tooltip title={params.row.assigne} arrow placement="top">
            <span className={styles.cellText}>{params.row.assigne}</span>
          </Tooltip>
        ) : (
          renderEmpty()
        ),
    },
    // Final Assigned Agent
    {
      field: 'finalAssignee',
      headerName: 'Assigné final',
      flex: w.assigne,
      minWidth: 90,
      renderCell: (params) =>
        params.row.lastAssigne ? (
          <Tooltip title={params.row.lastAssigne} arrow placement="top">
            <span className={styles.cellText}>{params.row.lastAssigne}</span>
          </Tooltip>
        ) : (
          renderEmpty()
        ),
    },

    {
      field: 'Rate',
      headerName: 'Note',
      type: 'number',
      flex: w.rate,
      minWidth: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const rate = params?.row?.rate;
        if (!rate) {
          return <span className={`${styles.noteCell} ${styles.noteEmpty}`}>—</span>;
        }
        return (
          <span className={`${styles.noteCell} ${getNoteClass(rate)}`}>
            {rate}
          </span>
        );
      },
    },

    {
      field: 'auteur',
      headerName: 'Auteur',
      flex: w.auteur,
      minWidth: 90,
      renderCell: (params) =>
        params?.row?.iUser ? (
          <Tooltip title={params.row.iUser} arrow placement="top">
            <span className={styles.cellText}>{params.row.iUser}</span>
          </Tooltip>
        ) : (
          renderEmpty()
        ),
    },

    {
      field: 'canal',
      headerName: 'Canal',
      flex: w.canal,
      minWidth: 80,
      renderCell: (params) => {
        const canal = params?.row?.canal
          ? translateCanalIndexLabel(Number(params.row.canal))
          : null;
        return canal ? (
          <Tooltip title={canal} arrow placement="top">
            <span className={styles.cellMuted}>{canal}</span>
          </Tooltip>
        ) : (
          renderEmpty()
        );
      },
    },

    {
      field: 'DélaiRésolution',
      headerName: 'Estimation',
      flex: w.delai,
      minWidth: 100,
      renderCell: (params) => {
        const delai = params.row.delaiResolution
          ? translateDelaiIndexLabel(Number(params.row.delaiResolution))
          : null;
        return delai ? (
          <Tooltip title={delai} arrow placement="top">
            <span className={styles.cellMuted}>{delai}</span>
          </Tooltip>
        ) : (
          renderEmpty()
        );
      },
    },

    {
      field: 'statut',
      headerName: 'Statut',
      align: 'center',
      headerAlign: 'center',
      flex: w.statut,
      minWidth: 90,
      renderCell: (params) => {
        const foundStatut = array_status.find(
          (statut) => statut.id === params.row.statut
        );
        return <StatusTicket foundStatut={foundStatut} />;
      },
    },

    {
      field: 'statutIntern',
      headerName: 'Statut Interne',
      align: 'center',
      headerAlign: 'center',
      flex: w.statutInt,
      minWidth: 90,
      renderCell: (params) => (
        <InternalStatusCell statutIntern={params.row.statutIntern} />
      ),
    },

    {
      field: 'action',
      align: 'center',
      headerAlign: 'center',
      headerName: 'Actions',
      flex: w.action,
      minWidth: 90,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const ticket = params.row;
        const statut = ticket.statut;

        const View = (
          <ActionButton tooltip="Voir" onClick={() => handleView(ticket.id)}>
            <ViewIcon />
          </ActionButton>
        );

        const Edit = (
          <ActionButton tooltip="Modifier" onClick={() => handleEdit(ticket.id)}>
            <EditIcon />
          </ActionButton>
        );

        const Execute = (
          <ActionButton
            tooltip="Exécuter"
            variant="execute"
            onClick={() => handleExecute(ticket.id)}
          >
            <ExecuteIcon color={Colors.black} />
          </ActionButton>
        );

        return (
          <div className={styles.actionGroup}>
            {statut === 0 && (
              <>
                {View}
                {Execute}
              </>
            )}
            {(statut === 1 || statut === 4) && (
              <>
                {View}
                {Edit}
              </>
            )}
            {statut === 6 && View}
            {![0, 1, 4, 6].includes(statut) && (
              <>
                {View}
                {Edit}
              </>
            )}
          </div>
        );
      },
    },
  ];
};